/* eslint-disable max-len */
import {Flex} from 'antd';
import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef
} from 'react';
import {useSearchParams} from 'react-router-dom';
import {
  createEditor,
  Transforms,
  Element,
  Node,
  Text,
  Path,
  Editor,
  Range
} from 'slate';
import {withHistory, HistoryEditor} from 'slate-history';
import {Slate, ReactEditor, withReact} from 'slate-react';

import {CommentPanel} from '../../components/CommentPanel/CommentPanel';
import Loader from '../../components/Loader/Loader';
import {ImageCropper} from '../../components/ReportBuilder/common';
import {Sidebar} from '../../components/Sidebar/Sidebar';
import {
  withImages,
  withLinks,
  withLists,
  withPageBreaks,
  withColumns,
  withPageNumbers,
  withPagination,
  withTables,
  withLineSpacing,
  LIST_TYPES
} from '../../components/Toolbar/constants';
import {
  insertColumnsInSelection,
  removeColumns
} from '../../components/Toolbar/createColumns';
import {Toolbar} from '../../components/Toolbar/Toolbar';
import {
  ColorPickerModal,
  HeaderFooterModal,
  PageMarginsModal,
  PageNumberModal,
  WatermarkModal
} from '../../components/Toolbar/ToolbarComponents';
import {APITypeEnum} from '../../services/interface/tableAPIType';
import {
  gerReportDetails,
  updateReportName
} from '../../services/modules/ReportBuilder/ReportBuilderAPI';
import {useCommentStore} from '../../store/reportStore/commentStore';
import useDocumentStore from '../../store/reportStore/documentStore';
import {useEditorStore} from '../../store/reportStore/editorStore';
import {reportDialog} from '../../store/reportStore/reportDialogStore';
import {useReportUserStore} from '../../store/reportStore/reportUserStore';
import useUserStore from '../../store/userStore/userStore';
import showMessage from '../../utils/showMessage';

import ReportDialogHost from './components/ReportDialogHost';
import {StatusBar} from './components/StatusBar';
import {MAX_MARGIN_IN} from './constants/CommonConstants';
import {
  applyLinkUrl,
  clearSidebarContent,
  handlePrint,
  insertHeader,
  insertFooter,
  insertImage,
  insertPageNumber,
  processInsertContent,
  clearDocument,
  toggleMark as applyToggleMark,
  deleteFooter,
  deleteHeader,
  removePageNumbers
} from './constants/CommonFunctions';
import {EditorUIContext} from './editor-core/contexts/EditorUIContext';
import EditorPage from './EditorPage';
import {useMultiSelection} from './hooks/useMultiSelection';
import {useReportDocumentSave} from './hooks/useReportDocumentSave';
import {
  applyCommentThreadToRange,
  createCommentId,
  createThreadId,
  getThreadIdFromComment,
  getThreadIdsInRange,
  isRangeFullyInThread,
  removeAllEmptyNewDrafts,
  removeEmptyDraft,
  withComments,
  type RawComment
} from './utils/commentUtils';
import {layoutDocument} from './utils/layoutEngine';
import {validateNode} from './utils/nodeHelpers';
import {exportToPDF} from './utils/pdfExporter';
import {getEditorSelectionAnchorRect} from './utils/reportDialogAnchors';
import {
  deselectIfInvalid,
  isRangeValid,
  withSelectionGuard,
  captureHeaderFooterSelectionMeta,
  restoreHeaderFooterSelection
} from './utils/selectionHelpers';
import {exportToWord} from './utils/wordExporter';

const DEFAULT_PAGE_MARGINS = {top: 1, right: 1, bottom: 1, left: 1};
const DEFAULT_PAGE_ORIENTATION: 'portrait' | 'landscape' = 'portrait';
const DEFAULT_WATERMARK = {
  text: '',
  color: '#000000',
  fontSize: 40,
  opacity: 0.2,
  layout: 'diagonal',
  fontFamily: 'Inter'
} as const;

const RepoortEditor: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [commentPanelOpen, setCommentPanelOpen] = useState(false);
  const [toolbarExpanded, setToolbarExpanded] = useState(true);
  const [croppingImage, setCroppingImage] = useState<{
    src: string;
    nodePath: any;
  } | null>(null);

  const MIN_FRACTION = 0.2;
  const MAX_FRACTION = 0.45;

  const reflowTimeoutRef = useRef<any>(null);
  const isReflowingRef = useRef(false);
  const lastSelectionRef = useRef<Range | null>(null);
  const lastCursorSelectionRef = useRef<Range | null>(null);
  const pendingBulkInsertRef = useRef(false);
  const measurementsCacheRef = useRef<Map<string, number>>(new Map());

  const [sidebarWidth, setSidebarWidth] = useState<number>(MIN_FRACTION);
  const [commentWidth, setCommentWidth] = useState<number>(MIN_FRACTION);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [isHeaderFooterEditMode, setIsHeaderFooterEditMode] = useState(false);

  type ActivePanel =
    | {
        id: 'colorPicker';
        colorType: 'text' | 'highlight' | 'block-bg';
        anchorRect: DOMRect | null;
        target: Range | null;
      }
    | {id: 'watermark'; anchorRect: DOMRect | null}
    | {id: 'pageNumber'; anchorRect: DOMRect | null}
    | {id: 'margins'; anchorRect: DOMRect | null}
    | {id: 'header'; anchorRect: DOMRect | null}
    | {id: 'footer'; anchorRect: DOMRect | null}
    | null;

  const [activePanel, setActivePanel] = useState<ActivePanel>(null);
  const closePanel = (): void => {
    setActivePanel(null);
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const reportId = searchParams.get('id') ?? '';
  const loadedReportIdRef = useRef<string | null>(null);
  const [reportData, setReportData] = useState<any>({});
  const [, setMetadata] = useState<{
    documentId: string | null;
    title: string;
    updatedAt: string;
  }>({
    documentId: null,
    title: 'Untitled Document',
    updatedAt: new Date().toISOString()
  });
  const [content, setContent] = useState<Node[]>([
    {
      type: 'page',
      id: 'page-0',
      pageNumber: 0,
      children: [{type: 'paragraph', children: [{text: ''}]}]
    } as any
  ]);
  const [slateKey, setSlateKey] = useState('doc-initial');
  const [pageMargins, setPageMargins] = useState(DEFAULT_PAGE_MARGINS);
  const [pageOrientation, setPageOrientation] = useState<
    'portrait' | 'landscape'
  >(DEFAULT_PAGE_ORIENTATION);
  const [watermark, setWatermark] = useState(DEFAULT_WATERMARK);
  const [screenshotState, setScreenshotState] = useState<{
    isOpen: boolean;
    imageSrc: string | null;
  }>({isOpen: false, imageSrc: null});

  const [isLoaded, setIsLoaded] = useState(false);

  const {saveDocument} = useReportDocumentSave({
    content,
    reportData,
    searchParams,
    setSearchParams,
    setReportData,
    isLoaded
  });

  const activeUser = useReportUserStore((s) => s.activeUser);
  const fetchReportBuilderUsers = useReportUserStore((s) => s.fetchUsers);
  const loggedInUserDetails = useUserStore((s) => s.userDetails);

  const handleRenameReport = useCallback(
    async (name: string) => {
      const id = reportData?._id as string | undefined;
      if (!id) return;

      const prevFilename = reportData?.filename;
      setReportData((prev: any) => ({...prev, filename: name}));

      try {
        await updateReportName(id, name);
      } catch (err) {
        setReportData((prev: any) => ({...prev, filename: prevFilename}));
      }
    },
    [reportData?._id, reportData?.filename]
  );

  const setActiveCommentNodeId = useCommentStore(
    (s) => s.setActiveCommentNodeId
  );
  const activeCommentNodeId = useCommentStore((s) => s.activeCommentNodeId);

  const editor = useMemo(
    () =>
      withSelectionGuard(
        withPagination(
          withLineSpacing(
            withLists(
              withPageBreaks(
                withPageNumbers(
                  withColumns(
                    withTables(
                      withImages(
                        withLinks(
                          withComments(withHistory(withReact(createEditor())))
                        )
                      )
                    )
                  )
                )
              )
            )
          )
        )
      ),
    []
  );
  // Keep editor-level flag in sync so element renderers can toggle
  // contentEditable for header/footer wrappers on double-click edit mode.
  Reflect.set(
    editor as object,
    'isHeaderFooterEditMode',
    isHeaderFooterEditMode
  );

  const handleSetCommentPanelOpen = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        const activeId = useCommentStore.getState().activeCommentNodeId;
        if (activeId) {
          removeEmptyDraft(editor, activeId);
        }
        setActiveCommentNodeId(null);
      }
      setCommentPanelOpen(isOpen);
    },
    [editor, setActiveCommentNodeId]
  );

  useEffect(() => {
    const loadKey = reportId || '__new__';

    if (loadedReportIdRef.current === loadKey) {
      return;
    }

    let cancelled = false;

    const init = async () => {
      try {
        const res = await gerReportDetails(
          APITypeEnum.GET_ONE,
          reportId || undefined
        );
        if (cancelled) return;

        setReportData(res ?? {});

        const resolvedId = res?._id as string | undefined;
        const effectiveLoadKey = resolvedId ?? loadKey;

        if (resolvedId && resolvedId !== reportId) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.set('id', resolvedId);

            return next;
          });
        }

        const apiDoc = res?.document;
        const saveStatus = useDocumentStore.getState().saveStatus;
        const isDirty = saveStatus === 'modified' || saveStatus === 'saving';
        const isSameDocumentReload =
          loadedReportIdRef.current !== null &&
          loadedReportIdRef.current === effectiveLoadKey;

        if (
          Array.isArray(apiDoc) &&
          apiDoc.length > 0 &&
          !(isDirty && isSameDocumentReload)
        ) {
          const validated = apiDoc.map((n: any): Node => {
            return validateNode(n);
          });
          setContent(validated);
          setSlateKey(`doc-${resolvedId ?? 'new'}-${Date.now()}`);
          HistoryEditor.withoutSaving(editor, () => {
            Editor.withoutNormalizing(editor, () => {
              editor.children = validated as Editor['children'];
              editor.selection = null;
            });
          });
        }

        loadedReportIdRef.current = effectiveLoadKey;

        setMetadata({
          documentId: resolvedId ?? null,
          title: (res?.filename as string) ?? 'Untitled Document',
          updatedAt: res?.updatedAt ?? new Date().toISOString()
        });
      } catch {
        if (!cancelled) {
          setIsLoaded(false);
        }

        return;
      }

      if (!cancelled) {
        setIsLoaded(true);
      }
    };

    void init();

    return () => {
      cancelled = true;
    };
  }, [reportId, editor]);

  const onCommentAdded = useCallback(() => {
    void saveDocument('comment', editor.children as Node[]);
  }, [saveDocument, editor]);

  const {selectedRanges, clearSelections, decorate} = useMultiSelection(editor);

  const useToolbarSync = (
    editor: Editor
  ): {toolbarSync: any; syncToolbar: () => void} => {
    const [toolbarSync, setToolbarSync] = useState<any>({
      fontSize: 12,
      fontFamily: 'Inter',
      color: '#000000',
      highlight: 'transparent',
      bold: false,
      italic: false,
      underline: false,
      align: 'left'
    });

    const sync = useCallback(() => {
      if (!editor.selection) return;
      if (!isRangeValid(editor, editor.selection)) {
        deselectIfInvalid(editor);

        return;
      }

      let marks: Record<string, unknown> = {};
      try {
        marks = Editor.marks(editor) ?? {};
      } catch {
        return;
      }

      let block: [any, Path] | undefined;
      try {
        block = Array.from(
          Editor.nodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              !['page', 'header-wrapper', 'footer-wrapper'].includes(n.type) &&
              Editor.isBlock(editor, n)
          })
        )[0] as [any, Path] | undefined;
      } catch {
        return;
      }

      const blockNode = block?.[0];
      const blockElement = Element.isElement(blockNode) ? blockNode : null;

      setToolbarSync({
        fontSize: Number(marks.fontSize) || 12,
        fontFamily: marks.fontFamily || 'Inter',
        color: marks.color || '#000000',
        highlight: marks.highlight || 'transparent',
        bold: !!marks.bold,
        italic: !!marks.italic,
        underline: !!marks.underline,
        strikethrough: !!marks.strikethrough,
        subscript: !!marks.subscript,
        superscript: !!marks.superscript,
        align: blockElement?.align ?? 'left',
        blockType: blockElement?.type ?? 'paragraph',
        selectionUpdated: Date.now()
      });
    }, [editor]);

    useEffect(() => {
      sync();
    }, [editor.selection, sync]);

    return {toolbarSync, syncToolbar: sync};
  };
  const {toolbarSync, syncToolbar: updateSync} = useToolbarSync(editor);

  const urlSelectedYear = searchParams.get('selectedYear');

  useLayoutEffect(() => {
    deselectIfInvalid(editor);
  });

  useEffect(() => {
    deselectIfInvalid(editor);
  }, [editor, slateKey, urlSelectedYear]);

  const pendingInsert = useEditorStore((s) => s.pendingInsert);
  const pendingClear = useEditorStore((s) => s.pendingClear);

  useEffect(() => {
    void fetchReportBuilderUsers();
  }, [fetchReportBuilderUsers]);

  useEffect(() => {
    if (!pendingInsert) return;
    processInsertContent(editor, pendingInsert.content, pendingBulkInsertRef);
    useEditorStore.getState().consumeInsert();
  }, [pendingInsert, editor]);

  useEffect(() => {
    if (!pendingClear) return;
    useEditorStore.getState().consumeClear();

    reportDialog.confirm({
      title: 'Clear sidebar content',
      message:
        'Are you sure you want to clear all contents inserted from the sidebar?',
      variant: 'danger',
      confirmLabel: 'Clear',
      onConfirm: () => {
        clearSidebarContent(editor);
      }
    });
  }, [pendingClear, editor]);

  // const startResize = (
  //   e: React.MouseEvent<HTMLDivElement>,
  //   isSideBar: boolean
  // ) => {
  //   e.preventDefault();
  //   const handleMove = (moveEvent: MouseEvent) => {
  //     const next = moveEvent.clientX / window.innerWidth;
  //     const clamped = Math.max(MIN_FRACTION, Math.min(MAX_FRACTION, next));
  //     if (isSideBar) {
  //       setSidebarWidth(clamped);
  //     } else {
  //       setCommentWidth(clamped);
  //     }
  //   };
  //   const stop = () => {
  //     setIsResizing(false);
  //     window.removeEventListener('mousemove', handleMove);
  //     window.removeEventListener('mouseup', stop);
  //   };
  //   setIsResizing(true);
  //   window.addEventListener('mousemove', handleMove);
  //   window.addEventListener('mouseup', stop);
  // };

  const isPathInsideHeaderFooter = useCallback(
    (nodePath: Path) => {
      try {
        const ancestors = Node.ancestors(editor, nodePath);
        let next;
        while (!(next = ancestors.next()).done) {
          const [ancestor] = next.value as any;
          if (
            Element.isElement(ancestor) &&
            (ancestor.type === 'header-wrapper' ||
              ancestor.type === 'footer-wrapper')
          )
            return true;
        }
      } catch (e) {}

      return false;
    },
    [editor]
  );

  const getActionRanges = useCallback((): Range[] => {
    const normalizeRange = (range: Range | null): Range | null => {
      if (!range?.anchor || !range.focus) return null;
      try {
        if (
          !Editor.hasPath(editor, range.anchor.path) ||
          !Editor.hasPath(editor, range.focus.path)
        )
          return null;

        return {
          anchor: Editor.start(editor, range),
          focus: Editor.end(editor, range)
        };
      } catch {
        return null;
      }
    };

    const ranges: Range[] = [];
    if (selectedRanges.length > 0) {
      selectedRanges.forEach((r: Range) => {
        const safeRange = normalizeRange(r);
        if (safeRange) ranges.push(safeRange);
      });
    }

    if (ranges.length === 0 && editor.selection) {
      const safe = normalizeRange(editor.selection);
      if (safe) ranges.push(safe);
    }

    if (ranges.length === 0 && lastSelectionRef.current) {
      const safeRange = normalizeRange(lastSelectionRef.current);
      if (safeRange) ranges.push(safeRange);
    }

    return ranges;
  }, [editor, selectedRanges]);

  const toggleMark = (format: string): void => {
    const ranges = getActionRanges();
    if (ranges.length === 0) return;

    let marks: Record<string, unknown> = {};
    try {
      if (editor.selection && isRangeValid(editor, editor.selection)) {
        marks = Editor.marks(editor) ?? {};
      }
    } catch {
      return;
    }
    const isActive = !!marks[format];

    applyToggleMark(editor, {
      format,
      isActive,
      ranges,
      isHeaderFooterEditMode,
      isPathInsideHeaderFooter
    });
    updateSync();
  };

  const toggleBlock = (format: string): void => {
    const isActive = toolbarSync.blockType === format;
    const ranges = getActionRanges();
    Editor.withoutNormalizing(editor, () => {
      ranges.forEach((r) => {
        if (isPathInsideHeaderFooter(r.anchor.path) && !isHeaderFooterEditMode)
          return;
        Transforms.setNodes(
          editor,
          {type: isActive ? 'paragraph' : format},
          {
            at: r,
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              !['link', 'page', 'header-wrapper', 'footer-wrapper'].includes(
                n.type
              )
          }
        );
      });
    });
  };

  const handleCropComplete = (croppedImageBase64: string): void => {
    Transforms.insertNodes(editor, [
      {
        type: 'image',
        url: croppedImageBase64,
        children: [{text: ''}]
      } as any,
      {type: 'paragraph', children: [{text: ''}]} as any
    ]);
    setScreenshotState({isOpen: false, imageSrc: null});
  };

  const setAlignment = (align: string): void => {
    const ranges = getActionRanges();
    Editor.withoutNormalizing(editor, () => {
      ranges.forEach((r) => {
        if (isPathInsideHeaderFooter(r.anchor.path) && !isHeaderFooterEditMode)
          return;
        Transforms.setNodes(
          editor,
          {align},
          {
            at: r,
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              [
                'paragraph',
                'heading-one',
                'heading-two',
                'table-cell',
                'list-item',
                'image',
                'placeholder-image',
                'shape'
              ].includes(n.type)
          }
        );
      });
    });
    ReactEditor.focus(editor);
  };
  const setFontSize = (size: number | string): void => {
    const ranges = getActionRanges();
    Editor.withoutNormalizing(editor, () => {
      ranges.forEach((r) => {
        if (isPathInsideHeaderFooter(r.anchor.path) && !isHeaderFooterEditMode)
          return;
        Transforms.setNodes(
          editor,
          {fontSize: Number(size)},
          {
            at: r,
            match: (n) => Text.isText(n),
            split: true
          }
        );
      });
    });
    ReactEditor.focus(editor);
  };

  const setFontFamily = (font: string): void => {
    const ranges = getActionRanges();
    Editor.withoutNormalizing(editor, () => {
      ranges.forEach((r) => {
        if (isPathInsideHeaderFooter(r.anchor.path) && !isHeaderFooterEditMode)
          return;
        Transforms.setNodes(
          editor,
          {fontFamily: font},
          {
            at: r,
            match: (n) => Text.isText(n),
            split: true
          }
        );
      });
    });
    ReactEditor.focus(editor);
  };

  const toggleColor = (type: string, color: string): void => {
    const ranges = getActionRanges();
    Editor.withoutNormalizing(editor, () => {
      ranges.forEach((r) => {
        if (isPathInsideHeaderFooter(r.anchor.path) && !isHeaderFooterEditMode)
          return;
        Transforms.setNodes(
          editor,
          {[type]: color},
          {
            at: r,
            match: (n) => Text.isText(n),
            split: true
          }
        );
      });
    });
    ReactEditor.focus(editor);
  };

  const insertLink = (): void => {
    const anchorRect = getEditorSelectionAnchorRect(editor);
    let defaultName = '';
    try {
      if (
        editor.selection &&
        isRangeValid(editor, editor.selection) &&
        !Range.isCollapsed(editor.selection)
      ) {
        defaultName = Editor.string(editor, editor.selection);
      }
    } catch {
      defaultName = '';
    }

    reportDialog.prompt({
      title: 'Insert link',
      confirmLabel: 'Insert',
      fields: [
        {
          id: 'name',
          label: 'Name',
          placeholder: 'Link text',
          defaultValue: defaultName
        },
        {
          id: 'url',
          label: 'URL',
          placeholder: 'https://example.com',
          required: true
        }
      ],
      ...(anchorRect
        ? {anchor: {type: 'rect' as const, rect: anchorRect}}
        : {}),
      onSubmit: (values: Record<string, string>) => {
        const trimmedName = values.name.trim();
        applyLinkUrl(editor, values.url, trimmedName || undefined);
      }
    });
  };

  useEffect(() => {
    if (editor.selection && isRangeValid(editor, editor.selection)) {
      const cloned = JSON.parse(JSON.stringify(editor.selection));
      lastCursorSelectionRef.current = cloned;
      if (!Range.isCollapsed(editor.selection)) {
        lastSelectionRef.current = cloned;
      }
    } else if (editor.selection) {
      deselectIfInvalid(editor);
    }

    if (
      !editor.selection ||
      !isRangeValid(editor, editor.selection) ||
      Range.isCollapsed(editor.selection) ||
      !activeCommentNodeId
    ) {
      return;
    }

    if (isRangeFullyInThread(editor, editor.selection, activeCommentNodeId)) {
      return;
    }

    removeEmptyDraft(editor, activeCommentNodeId);
    setActiveCommentNodeId(null);
  }, [editor.selection, activeCommentNodeId, editor, setActiveCommentNodeId]);

  const insertComment = (): void => {
    const ranges = getActionRanges();
    const range = ranges[0];
    if (!range) {
      showMessage({
        status: 'warning',
        message: 'Please place the cursor in the paragraph to comment on.'
      });

      return;
    }

    if (Range.isCollapsed(range)) {
      showMessage({
        status: 'warning',
        message: 'Please select text to comment on.'
      });

      return;
    }

    removeAllEmptyNewDrafts(editor);

    const threadIdsInRange = getThreadIdsInRange(editor, range);
    const reuseExisting =
      threadIdsInRange.length === 1 &&
      isRangeFullyInThread(editor, range, threadIdsInRange[0]);
    const threadId = reuseExisting ? threadIdsInRange[0] : createThreadId();

    const blockEntry = Editor.above(editor, {
      at: range,
      match: (n) =>
        !Editor.isEditor(n) && Element.isElement(n) && Editor.isBlock(editor, n)
    });
    if (!blockEntry) return;

    const [block, path] = blockEntry;
    const existingComments =
      'comments' in block &&
      Array.isArray((block as {comments?: unknown}).comments)
        ? ((block as {comments?: RawComment[]}).comments as RawComment[]) ??
          []
        : [];

    const threadComments = existingComments.filter(
      (c) => getThreadIdFromComment(c) === threadId
    );

    if (reuseExisting && threadComments.length > 0) {
      handleSetCommentPanelOpen(false);
      setActiveCommentNodeId(threadId);
      ReactEditor.focus(editor);

      return;
    }

    if (!reuseExisting) {
      applyCommentThreadToRange(editor, range, threadId);
    }

    const fallbackUserId =
      loggedInUserDetails?._id ?? loggedInUserDetails?.id ?? '';
    const fallbackFirstName = loggedInUserDetails?.firstName ?? '';
    const fallbackLastName = loggedInUserDetails?.lastName ?? '';
    const nameFromActiveUser = activeUser?.name?.trim() ?? '';
    const [nameFirstPart = '', ...nameRestParts] =
      nameFromActiveUser.split(' ');
    const now = new Date().toISOString();
    const newComment = {
      id: createCommentId(),
      threadId,
      text: '',
      comment: '',
      isNew: true,
      userId: activeUser?.id || fallbackUserId,
      user_id: activeUser?.id || fallbackUserId,
      userFirstName:
        activeUser?.firstName ||
        fallbackFirstName ||
        nameFirstPart ||
        'Unknown',
      userLastName:
        activeUser?.lastName ||
        fallbackLastName ||
        nameRestParts.join(' ') ||
        '',
      createdAt: now,
      updatedAt: now,
      updateAt: now
    };

    Transforms.setNodes(
      editor,
      {comments: [...existingComments, newComment]} as any,
      {at: path}
    );
    setActiveCommentNodeId(threadId);
    handleSetCommentPanelOpen(false);
    ReactEditor.focus(editor);
  };

  const isBlockActive = (
    editor: Editor,
    format: string,
    options: any = {}
  ): boolean => {
    const {at = editor.selection} = options;
    if (!at) return false;
    const [match] = Array.from(
      Editor.nodes(editor, {
        at,
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === format,
        mode: 'all'
      })
    );
    if (match) return true;

    const [above] = Array.from(
      Editor.nodes(editor, {
        at,
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === format,
        mode: 'highest'
      })
    );

    return !!above;
  };

  const toggleList = (
    editor: Editor,
    format: string,
    options: any = {}
  ): void => {
    const {at = editor.selection} = options;
    if (!at) return;
    const isActive = isBlockActive(editor, format, {at});
    Transforms.unwrapNodes(editor, {
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        LIST_TYPES.includes(n.type),
      split: true,
      at
    });

    if (isActive) {
      Transforms.setNodes(editor, {type: 'paragraph'} as any, {
        at,
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === 'list-item'
      });
    } else {
      Transforms.setNodes(editor, {type: 'list-item', indent: 1} as any, {
        at,
        match: (n) =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          ![
            'page',
            'header-wrapper',
            'footer-wrapper',
            'table',
            'table-row',
            'table-cell'
          ].includes(n.type) &&
          Editor.isBlock(editor, n)
      });
      Transforms.wrapNodes(editor, {type: format, children: []} as any, {
        at,
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === 'list-item'
      });
    }
  };

  const indentList = (editor: Editor, options: any = {}): void => {
    const at =
      options.at ??
      editor.selection ??
      lastCursorSelectionRef.current ??
      lastSelectionRef.current;
    if (!at) return;

    const matchedNode = Editor.above(editor, {
      at,
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        ['list-item', 'paragraph'].includes(n.type)
    });

    if (!matchedNode) return;
    const [node, path] = matchedNode as [any, number[]];

    if (node.type === 'list-item') {
      const currentIndent = Number(node.indent || 0);
      Transforms.setNodes(editor, {indent: currentIndent + 1} as any, {
        at: path
      });
    } else {
      const currentIndent = Number(node.firstLineIndent || 0);
      Transforms.setNodes(editor, {firstLineIndent: currentIndent + 1} as any, {
        at: path
      });
    }
    ReactEditor.focus(editor);
  };

  const outdentList = (editor: Editor, options: any = {}): void => {
    const at =
      options.at ??
      editor.selection ??
      lastCursorSelectionRef.current ??
      lastSelectionRef.current;
    if (!at) return;

    const matchedNode = Editor.above(editor, {
      at,
      match: (n) =>
        !Editor.isEditor(n) &&
        Element.isElement(n) &&
        ['list-item', 'paragraph'].includes(n.type)
    });

    if (!matchedNode) return;
    const [node, path] = matchedNode as [any, number[]];

    if (node.type === 'list-item') {
      const currentIndent = Number(node.indent || 0);
      if (currentIndent > 0) {
        Transforms.setNodes(
          editor,
          {indent: Math.max(0, currentIndent - 1)} as any,
          {at: path}
        );
      }
    } else {
      const currentIndent = Number(node.firstLineIndent || 0);
      if (currentIndent > 0) {
        Transforms.setNodes(
          editor,
          {firstLineIndent: Math.max(0, currentIndent - 1)} as any,
          {at: path}
        );
      }
    }
    ReactEditor.focus(editor);
  };

  const clearFormatting = (editor: Editor): void => {
    [
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'code',
      'superscript',
      'subscript',
      'textColor',
      'highlightColor'
    ].forEach((format) => {
      Transforms.unsetNodes(editor, format as any, {
        at: [],
        match: Text.isText,
        mode: 'all'
      });
    });
  };

  const normalizeMargins = (
    margins: any = DEFAULT_PAGE_MARGINS
  ): {top: number; right: number; bottom: number; left: number} => {
    const toNumber = (value: any, fallback: number): number => {
      const parsed = Number(value);
      if (!Number.isFinite(parsed)) return fallback;

      return Math.max(0, Math.min(MAX_MARGIN_IN, parsed));
    };

    return {
      top: toNumber(margins.top, DEFAULT_PAGE_MARGINS.top),
      right: toNumber(margins.right, DEFAULT_PAGE_MARGINS.right),
      bottom: toNumber(margins.bottom, DEFAULT_PAGE_MARGINS.bottom),
      left: toNumber(margins.left, DEFAULT_PAGE_MARGINS.left)
    };
  };

  const effectivePageMargins = useMemo(
    () => normalizeMargins(pageMargins),
    [pageMargins]
  );

  const getExportFilename = useCallback(
    (extension: 'pdf' | 'docx'): string => {
      const rawName =
        typeof reportData?.filename === 'string'
          ? reportData.filename.trim()
          : '';
      const safeBaseName = (rawName || 'Untitled Document').replace(
        /[<>:"/\\|?*]/g,
        '_'
      );

      return `${safeBaseName}.${extension}`;
    },
    [reportData?.filename]
  );

  const insertShape = (shapeType: string) => {
    // 1. Define A4 dimensions based on orientation (Portrait: 8.27x11.69, Landscape: 11.69x8.27)
    const sizeInches =
      pageOrientation === 'landscape'
        ? {width: 11.69, height: 8.27}
        : {width: 8.27, height: 11.69};

    // 2. Calculate usable width/height in pixels (96 DPI) using normalized margins
    const pageWidthPx =
      (sizeInches.width -
        effectivePageMargins.left -
        effectivePageMargins.right) *
      96;
    const pageHeightPx =
      (sizeInches.height -
        effectivePageMargins.top -
        effectivePageMargins.bottom) *
      96;

    let width = 200;
    let height = 150;

    switch (shapeType) {
      case 'logo':
        width = 100;
        height = 100;
        break;
      case 'icon':
        width = 50;
        height = 50;
        break;
      case 'banner':
        width = pageWidthPx;
        height = 200;
        break;
      case 'hero':
        width = pageWidthPx;
        height = pageHeightPx / 2;
        break;
      case 'thumbnail':
        width = 640;
        height = 360;
        break;
      case 'circle':
        width = 150;
        height = 150;
        break;
      default:
        break;
    }

    const shape = {
      type: 'placeholder-image',
      placeholderType: shapeType,
      width,
      height,
      children: [{text: ''}]
    };

    Transforms.insertNodes(editor, shape);
    Transforms.insertNodes(editor, {type: 'paragraph', children: [{text: ''}]});
  };

  const insertTable = (): void => {
    Transforms.insertNodes(editor, [
      {
        type: 'table',
        children: [
          {
            type: 'table-row',
            children: [
              {type: 'table-cell', children: [{text: 'Row 1, Col 1'}]},
              {type: 'table-cell', children: [{text: 'Row 1, Col 2'}]},
              {type: 'table-cell', children: [{text: 'Row 1, Col 3'}]}
            ]
          },
          {
            type: 'table-row',
            children: [
              {type: 'table-cell', children: [{text: ''}]},
              {type: 'table-cell', children: [{text: ''}]},
              {type: 'table-cell', children: [{text: ''}]}
            ]
          }
        ]
      },
      {type: 'paragraph', children: [{text: ''}]}
    ]);
  };

  const insertPageBreak = (): void => {
    Transforms.insertNodes(editor, [
      {type: 'page-break', children: [{text: ''}]},
      {type: 'paragraph', children: [{text: ''}]}
    ]);
    ReactEditor.focus(editor);
  };

  const insertColumns = (count: number, gap?: number): void => {
    insertColumnsInSelection(editor, count, gap);
    ReactEditor.focus(editor);
  };

  const removeColumnsFromSelection = (): void => {
    removeColumns(editor);
    ReactEditor.focus(editor);
  };

  const hashNode = useCallback((sN: any): string => {
    try {
      // Include first leaf's fontSize since block nodes carry font size on leaves
      const firstLeafFs =
        Array.isArray(sN.children) && sN.children.length > 0
          ? sN.children[0]?.fontSize
          : undefined;

      return JSON.stringify({
        type: sN.type,
        text: Node.string(sN),
        childCount: sN.children?.length ?? 0,
        width: sN.width,
        height: sN.height,
        lineHeight: sN.lineHeight,
        fontSize: sN.fontSize ?? firstLeafFs,
        align: sN.align,
        size: sN.size // for spacer nodes
      });
    } catch {
      return String(Math.random());
    }
  }, []);

  const sanitizeRange = (editor: Editor, range: Range): Range | null => {
    if (!range?.anchor || !range.focus) return null;
    try {
      if (
        !Editor.hasPath(editor, range.anchor.path) ||
        !Editor.hasPath(editor, range.focus.path)
      )
        return null;

      Node.leaf(editor, range.anchor.path);
      Node.leaf(editor, range.focus.path);

      return range;
    } catch {
      return null;
    }
  };

  const selectRangeSafely = useCallback(
    (range: Range | null) => {
      if (!range) return false;
      const safe = sanitizeRange(editor, range);
      if (!safe) {
        Transforms.deselect(editor);
        editor.selection = null;

        return false;
      }
      try {
        Transforms.select(editor, safe);

        return true;
      } catch (e) {
        Transforms.deselect(editor);
        editor.selection = null;

        return false;
      }
    },
    [editor]
  );

  const handleReflow = useCallback(() => {
    if (isReflowingRef.current) return;

    // Clear any pending reflow
    if (reflowTimeoutRef.current) clearTimeout(reflowTimeoutRef.current);

    reflowTimeoutRef.current = setTimeout(
      () => {
        if (isReflowingRef.current) return;
        isReflowingRef.current = true;

        try {
          deselectIfInvalid(editor);

          const sizeInches =
            pageOrientation === 'landscape'
              ? {width: 11.69, height: 8.27} // Landscape
              : {width: 8.27, height: 11.69}; // Portrait

          const sizePx = {
            width: sizeInches.width * 96,
            height: sizeInches.height * 96
          };
          const marginsPx = {
            top: effectivePageMargins.top * 96,
            bottom: effectivePageMargins.bottom * 96,
            left: effectivePageMargins.left * 96,
            right: effectivePageMargins.right * 96
          };

          // ---------------------------------------------------------------
          // Measure every node via ReactEditor.toDOMNode — index-independent.
          // This correctly handles nodes that have been moved across pages by
          // a previous reflow, since toDOMNode looks up by Slate node identity
          // rather than by DOM position index.
          // ---------------------------------------------------------------
          const measureNode = (sN: any) => {
            if (!sN?.type) return;
            try {
              const domEl = ReactEditor.toDOMNode(editor, sN);
              if (!domEl) return;

              const rect = domEl.getBoundingClientRect();
              if (rect.height <= 0) return;

              const cs = window.getComputedStyle(domEl);
              const mt = parseFloat(cs.marginTop) || 0;
              const mb = parseFloat(cs.marginBottom) || 0;
              const h = rect.height + mt + mb;

              if (h > 0) measurementsCacheRef.current.set(hashNode(sN), h);

              // Recurse into children for containers so sub-nodes get cached
              if (
                Array.isArray(sN.children) &&
                sN.children.length > 0 &&
                sN.type !== 'image' &&
                sN.type !== 'placeholder-image' &&
                sN.type !== 'shape' &&
                sN.type !== 'spacer' &&
                sN.type !== 'page-break' &&
                sN.type !== 'page-number'
              ) {
                sN.children.forEach((child: any) => {
                  measureNode(child);
                });
              }
            } catch {
              // toDOMNode throws if node isn't mounted yet — skip silently
            }
          };

          editor.children.forEach((page: any) => {
            if (!Array.isArray(page.children)) return;
            page.children.forEach((node: any) => {
              measureNode(node);
            });
          });

          // Build a hash-keyed measurements proxy for layoutDocument
          const measurements = {
            get: (node: any) =>
              measurementsCacheRef.current.get(hashNode(node)),
            set: (node: any, val: number) =>
              measurementsCacheRef.current.set(hashNode(node), val)
          };

          let activeH = null;
          let activeF = null;
          if (editor.selection && isHeaderFooterEditMode) {
            const nodeEntries = Editor.nodes(editor, {
              at: editor.selection,
              match: (n) =>
                !Editor.isEditor(n) &&
                Element.isElement(n) &&
                (n.type === 'header-wrapper' || n.type === 'footer-wrapper')
            });
            const match = nodeEntries.next().value as any;
            if (match) {
              if (match[0].type === 'header-wrapper') activeH = match[0];
              if (match[0].type === 'footer-wrapper') activeF = match[0];
            }
          }

          const flat = [];
          const isPaged =
            editor.children.length > 0 &&
            (editor.children[0] as any).type === 'page';

          let headerTpl = null;
          let footerTpl = null;

          if (isPaged) {
            const firstPage = editor.children[0] as any;
            headerTpl = firstPage.children.find(
              (n: any) => n.type === 'header-wrapper'
            );
            footerTpl = firstPage.children.find(
              (n: any) => n.type === 'footer-wrapper'
            );

            editor.children.forEach((p) => {
              if (Element.isElement(p) && Array.isArray(p.children)) {
                flat.push(...p.children);
              }
            });
          } else {
            flat.push(...editor.children);
          }

          const newPages = layoutDocument(
            flat,
            measurements,
            sizePx,
            marginsPx,
            activeH || headerTpl,
            activeF || footerTpl
          );
          const normalized = newPages.map((p: any, i) => ({
            type: 'page',
            children: p.children ||
              p.nodes || [{type: 'paragraph', children: [{text: ''}]}],
            id: p.id || `page-${i}`,
            pageNumber: p.pageNumber || i,
            height: p.height || 0,
            isDirty: p.isDirty || false
          }));

          const currentChildrenStr = JSON.stringify(
            editor.children.map((p) => p.children)
          );
          const normalizedChildrenStr = JSON.stringify(
            normalized.map((p) => p.children)
          );

          if (currentChildrenStr !== normalizedChildrenStr) {
            const oldSel =
              editor.selection && isRangeValid(editor, editor.selection)
                ? editor.selection
                : null;

            // Capture the actual nodes being selected to track them after reflow
            let selectionNodes: any = null;
            let hfSelectionMeta: any = null;
            if (oldSel) {
              try {
                if (isHeaderFooterEditMode) {
                  hfSelectionMeta = captureHeaderFooterSelectionMeta(
                    editor,
                    oldSel
                  );
                }
                if (
                  !hfSelectionMeta &&
                  !isPathInsideHeaderFooter(oldSel.anchor.path)
                ) {
                  selectionNodes = {
                    anchor: Node.get(editor, oldSel.anchor.path),
                    focus: Node.get(editor, oldSel.focus.path)
                  };
                }
              } catch (e) {}
            }

            // CRITICAL: We MUST nullify the selection on the editor object itself
            // before changing children. This prevents components (like Toolbar)
            // from attempting to access marks at invalid paths during the re-render.
            editor.selection = null;
            Transforms.deselect(editor);

            HistoryEditor.withoutSaving(editor, () => {
              Editor.withoutNormalizing(editor, () => {
                editor.children = normalized;
                editor.onChange();
              });
            });

            if (oldSel) {
              // Wrap restoration in another timeout to ensure DOM has settled
              setTimeout(() => {
                try {
                  if (hfSelectionMeta) {
                    restoreHeaderFooterSelection(editor, hfSelectionMeta);

                    return;
                  }

                  // Try to restore based on node identity first (the "soft page" advantage)
                  if (selectionNodes) {
                    try {
                      const newAnchorPath = ReactEditor.findPath(
                        editor,
                        selectionNodes.anchor
                      );
                      const newFocusPath = ReactEditor.findPath(
                        editor,
                        selectionNodes.focus
                      );
                      if (newAnchorPath && newFocusPath) {
                        Transforms.select(editor, {
                          anchor: {
                            path: newAnchorPath,
                            offset: oldSel.anchor.offset
                          },
                          focus: {
                            path: newFocusPath,
                            offset: oldSel.focus.offset
                          }
                        });
                      }
                    } catch (e) {}
                  }
                } catch (e) {
                  Transforms.deselect(editor);
                }
              }, 0);
            }
          }
        } catch (err) {
          console.error('Reflow error:', err);
        } finally {
          isReflowingRef.current = false;
          if (pendingBulkInsertRef.current) {
            pendingBulkInsertRef.current = false;
          }
        }
      },
      pendingBulkInsertRef.current ? 300 : 100
    ); // longer wait after bulk insert
  }, [
    editor,
    pageOrientation,
    effectivePageMargins,
    selectRangeSafely,
    hashNode,
    isHeaderFooterEditMode
  ]);

  useEffect(() => {
    isReflowingRef.current = false;
    if (reflowTimeoutRef.current) clearTimeout(reflowTimeoutRef.current);
    handleReflow();
  }, [pageOrientation, effectivePageMargins]);

  const handleChange = (val: Node[]): void => {
    deselectIfInvalid(editor);
    setContent(val);

    requestAnimationFrame(() => {
      handleReflow();
      handleReflow();
    });
    if (!pendingBulkInsertRef.current && !isReflowingRef.current) {
      updateSync();
    }
  };

  const startSidebarResize = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const handleMove = (moveEvent: MouseEvent) => {
      const next = moveEvent.clientX / window.innerWidth;
      const clamped = Math.max(MIN_FRACTION, Math.min(MAX_FRACTION, next));
      setSidebarWidth(clamped);
    };

    const stop = () => {
      setIsResizing(false);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stop);
    };

    setIsResizing(true);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stop);
  };

  const startCommentResize = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    const handleMove = (moveEvent: MouseEvent) => {
      const fromRight = window.innerWidth - moveEvent.clientX;
      const next = fromRight / window.innerWidth;
      const clamped = Math.max(MIN_FRACTION, Math.min(MAX_FRACTION, next));
      setCommentWidth(clamped);
    };

    const stop = () => {
      setIsResizing(false);
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', stop);
    };

    setIsResizing(true);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', stop);
  };

  if (!isLoaded) {
    return <Loader isHome={true} description="Loading Document..." />;
  }

  return (
    <Slate
      key={slateKey}
      editor={editor}
      initialValue={content as any}
      onChange={handleChange}
    >
      <Flex
        className="reportBuilderLayout editorContainer"
        align="center"
        justify="space-between"
        vertical
      >
        <Toolbar
          editor={editor}
          toggleMark={toggleMark}
          toggleBlock={toggleBlock}
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          setAlignment={setAlignment}
          setFontSize={setFontSize}
          setFontFamily={setFontFamily}
          toggleColor={toggleColor}
          insertLink={insertLink}
          insertComment={insertComment}
          handleToggleList={(f: string) => {
            toggleList(editor, f);
          }}
          saveDocument={saveDocument}
          selectedRanges={selectedRanges}
          toolbarSync={toolbarSync}
          indentList={() => {
            indentList(editor);
          }}
          reportData={reportData}
          onRenameReport={handleRenameReport}
          outdentList={() => {
            outdentList(editor);
          }}
          commentPanelOpen={commentPanelOpen}
          setCommentPanelOpen={handleSetCommentPanelOpen}
          toolbarExpanded={toolbarExpanded}
          setToolbarExpanded={setToolbarExpanded}
          isHeaderFooterEditMode={isHeaderFooterEditMode}
          clearFormatting={clearFormatting}
          isMarkActive={(_ed, f) => !!toolbarSync?.[f]}
          getActiveBlock={() => toolbarSync.blockType}
          getActiveAlignment={() => toolbarSync.align}
          triggerImageUpload={() =>
            document.getElementById('image-upload')?.click()
          }
          insertShape={insertShape}
          insertTable={insertTable}
          insertColumns={insertColumns}
          removeColumns={removeColumnsFromSelection}
          startScreenCapture={() => {
            navigator.mediaDevices
              .getDisplayMedia({
                video: {cursor: 'never'} as any,
                audio: false
              })
              .then((stream: MediaStream) => {
                const video = document.createElement('video');
                video.srcObject = stream;

                video.onloadedmetadata = () => {
                  video
                    .play()
                    .then(() => {
                      setTimeout(() => {
                        const canvas = document.createElement('canvas');
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const ctx = canvas.getContext('2d');

                        if (ctx) {
                          ctx.drawImage(
                            video,
                            0,
                            0,
                            canvas.width,
                            canvas.height
                          );
                        }

                        const capturedImage = canvas.toDataURL('image/png');

                        stream.getTracks().forEach((track) => {
                          track.stop();
                        });
                        video.srcObject = null;
                        setScreenshotState({
                          isOpen: true,
                          imageSrc: capturedImage
                        });
                      }, 500);
                    })
                    .catch((err) => {
                      console.error('Playback failed', err);
                    });
                };
              })
              .catch((err: Error) => {
                console.error('Screen capture failed:', err);
                if (err.name !== 'NotAllowedError') {
                  showMessage({
                    status: 'error',
                    message: `Failed to capture screen: ${err.message}`
                  });
                }
              });
          }}
          handleExportPDF={() => {
            void exportToPDF(
              content,
              pageOrientation,
              effectivePageMargins,
              getExportFilename('pdf')
            );
          }}
          handlePrint={handlePrint}
          handleExportWord={() => {
            void exportToWord(content, getExportFilename('docx'), watermark, {
              pageOrientation,
              pageMargins: effectivePageMargins
            });
          }}
          pageOrientation={pageOrientation}
          setPageOrientation={setPageOrientation}
          pageMargins={pageMargins}
          setPageMargins={setPageMargins}
          openWatermarkModal={(anchorEl) => {
            setActivePanel({
              id: 'watermark',
              anchorRect: anchorEl?.getBoundingClientRect() ?? null
            });
          }}
          openPageNumberModal={(anchorEl) => {
            setActivePanel({
              id: 'pageNumber',
              anchorRect: anchorEl?.getBoundingClientRect() ?? null
            });
          }}
          insertPageNumber={() => {
            Transforms.insertNodes(editor, {
              type: 'page-number',
              children: [{text: ''}]
            });
          }}
          removePageNumbers={() => {
            setIsHeaderFooterEditMode(false);
            removePageNumbers(editor);
          }}
          insertPageBreak={insertPageBreak}
          handleClearDocument={() => {
            reportDialog.confirm({
              title: 'Clear document',
              message: 'Clear document?',
              variant: 'danger',
              confirmLabel: 'Clear',
              onConfirm: () => {
                clearDocument(editor);
              }
            });
          }}
          insertHeader={(anchorEl) => {
            setActivePanel({
              id: 'header',
              anchorRect: anchorEl?.getBoundingClientRect() ?? null
            });
          }}
          insertFooter={(anchorEl) => {
            setActivePanel({
              id: 'footer',
              anchorRect: anchorEl?.getBoundingClientRect() ?? null
            });
          }}
          openMarginsModal={(anchorEl) => {
            setActivePanel({
              id: 'margins',
              anchorRect: anchorEl?.getBoundingClientRect() ?? null
            });
          }}
          setIsHeaderFooterEditMode={setIsHeaderFooterEditMode}
          openColorPicker={(type, anchorEl) => {
            setActivePanel({
              id: 'colorPicker',
              colorType: type,
              anchorRect: anchorEl?.getBoundingClientRect() ?? null,
              target:
                (editor.selection ?? selectedRanges[0]) ||
                lastSelectionRef.current ||
                null
            });
          }}
        />
        <input
          id="image-upload"
          type="file"
          accept="image/*"
          style={{display: 'none'}}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              void insertImage(editor, file);
            }
            e.target.value = '';
          }}
        />
        <Flex className="report-builder-main">
          <PageNumberModal
            isOpen={activePanel?.id === 'pageNumber'}
            anchorRect={
              activePanel?.id === 'pageNumber' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            onApply={(settings) => {
              insertPageNumber(editor, settings);
            }}
            onRemove={() => {
              setIsHeaderFooterEditMode(false);
              removePageNumbers(editor);
            }}
          />
          <PageMarginsModal
            isOpen={activePanel?.id === 'margins'}
            anchorRect={
              activePanel?.id === 'margins' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            onApply={(margins) => {
              setPageMargins(margins);
            }}
            initialMargins={pageMargins}
            pageOrientationLabel={pageOrientation}
          />
          <HeaderFooterModal
            isOpen={activePanel?.id === 'header'}
            anchorRect={
              activePanel?.id === 'header' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            onSelect={(type) => {
              if (type === 'remove') {
                setIsHeaderFooterEditMode(false);
                deleteHeader(editor);
              } else {
                insertHeader(editor, type);
                setIsHeaderFooterEditMode(true);
              }
            }}
            title="Select Header Template"
          />
          <HeaderFooterModal
            isOpen={activePanel?.id === 'footer'}
            anchorRect={
              activePanel?.id === 'footer' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            onSelect={(type) => {
              if (type === 'remove') {
                setIsHeaderFooterEditMode(false);
                deleteFooter(editor);
              } else {
                insertFooter(editor, type);
                setIsHeaderFooterEditMode(true);
              }
            }}
            title="Select Footer Template"
          />
          <WatermarkModal
            isOpen={activePanel?.id === 'watermark'}
            anchorRect={
              activePanel?.id === 'watermark' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            onApply={setWatermark}
            initialWatermark={watermark}
          />
          <ColorPickerModal
            isOpen={activePanel?.id === 'colorPicker'}
            anchorRect={
              activePanel?.id === 'colorPicker' ? activePanel.anchorRect : null
            }
            onClose={closePanel}
            title={
              activePanel?.id === 'colorPicker' &&
              activePanel.colorType === 'text'
                ? 'Text Color'
                : activePanel?.id === 'colorPicker' &&
                    activePanel.colorType === 'highlight'
                  ? 'Highlight Color'
                  : 'Block Background'
            }
            initialColor={
              activePanel?.id === 'colorPicker' &&
              activePanel.colorType === 'text'
                ? (toolbarSync?.color as string)
                : activePanel?.id === 'colorPicker' &&
                    activePanel.colorType === 'highlight'
                  ? (toolbarSync?.highlight as string)
                  : '#ffffff'
            }
            onChange={(color) => {
              if (activePanel?.id === 'colorPicker' && activePanel.target) {
                Transforms.select(editor, activePanel.target);
              }
              const colorType =
                activePanel?.id === 'colorPicker'
                  ? activePanel.colorType
                  : 'text';
              if (colorType === 'block-bg') {
                Transforms.setNodes(editor, {backgroundColor: color} as any, {
                  match: (n) =>
                    !Editor.isEditor(n) &&
                    Element.isElement(n) &&
                    !['page', 'header-wrapper', 'footer-wrapper'].includes(
                      n.type
                    ) &&
                    Editor.isBlock(editor, n)
                });
              } else {
                toggleColor(
                  colorType === 'text' ? 'color' : 'highlight',
                  color
                );
              }
            }}
          />

          <EditorUIContext.Provider
            value={{
              commentPanelOpen,
              setCommentPanelOpen: handleSetCommentPanelOpen,
              setCroppingImage,
              onCommentAdded
            }}
          >
            <Flex
              className={`app-container reportComponents ${
                isResizing ? 'is-resizing' : ''
              }`}
              vertical={false}
            >
              {screenshotState.isOpen && screenshotState.imageSrc && (
                <ImageCropper
                  imageSrc={screenshotState.imageSrc}
                  onCrop={handleCropComplete}
                  onCancel={() => {
                    setScreenshotState({isOpen: false, imageSrc: null});
                  }}
                />
              )}
              {croppingImage && (
                <ImageCropper
                  imageSrc={croppingImage.src}
                  onCrop={(croppedData) => {
                    Transforms.setNodes(
                      editor,
                      {
                        url: croppedData,
                        crop: {top: 0, left: 0, bottom: 0, right: 0}
                      },
                      {at: croppingImage.nodePath}
                    );
                    setCroppingImage(null);
                  }}
                  onCancel={() => {
                    setCroppingImage(null);
                  }}
                />
              )}

              {/* Center: Editor */}
              {sidebarOpen && (
                <div
                  className="sidebar-container"
                  style={{
                    width: `${sidebarWidth * 100}%`,
                    minWidth: `${MIN_FRACTION * 100}%`
                  }}
                >
                  <Sidebar />
                </div>
              )}

              {sidebarOpen && (
                <div
                  className="panel-resize-handle panel-resize-handle--left"
                  onMouseDown={startSidebarResize}
                />
              )}
              <div className="editor-area">
                <EditorPage
                  editor={editor}
                  toggleMark={toggleMark}
                  toggleBlock={toggleBlock}
                  setAlignment={setAlignment}
                  setFontSize={setFontSize}
                  setFontFamily={setFontFamily}
                  toggleColor={toggleColor}
                  insertLink={insertLink}
                  insertComment={insertComment}
                  toggleCommentPanel={() => {
                    handleSetCommentPanelOpen(!commentPanelOpen);
                  }}
                  handleToggleList={(f: string) => {
                    toggleList(editor, f);
                  }}
                  saveDocument={saveDocument}
                  clearSelections={clearSelections}
                  decorate={decorate}
                  toolbarSync={toolbarSync}
                  openColorPicker={(type, anchorEl) => {
                    setActivePanel({
                      id: 'colorPicker',
                      colorType: type,
                      anchorRect: anchorEl?.getBoundingClientRect() ?? null,
                      target:
                        (editor.selection ?? selectedRanges[0]) ||
                        lastSelectionRef.current ||
                        null
                    });
                  }}
                  indentList={indentList}
                  outdentList={outdentList}
                  isHeaderFooterEditMode={isHeaderFooterEditMode}
                  setIsHeaderFooterEditMode={setIsHeaderFooterEditMode}
                  isPathInsideHeaderFooter={isPathInsideHeaderFooter}
                />
                {screenshotState.isOpen && screenshotState.imageSrc && (
                  <ImageCropper
                    imageSrc={screenshotState.imageSrc}
                    onCrop={handleCropComplete}
                    onCancel={() => {
                      setScreenshotState({isOpen: false, imageSrc: null});
                    }}
                  />
                )}
                <StatusBar />
              </div>
              {commentPanelOpen && (
                <>
                  <div
                    className="panel-resize-handle panel-resize-handle--right"
                    onMouseDown={startCommentResize}
                  />
                  <div
                    className="comment-panel-container"
                    style={{width: `${commentWidth * 100}%`}}
                  >
                    <CommentPanel
                      onClose={() => {
                        handleSetCommentPanelOpen(false);
                      }}
                      content={content}
                      editor={editor}
                    />
                  </div>
                </>
              )}
            </Flex>
          </EditorUIContext.Provider>
        </Flex>
      </Flex>
      <ReportDialogHost />
    </Slate>
  );
};

export default RepoortEditor;
